import { routes } from '@/router/routes';
import { useUserStore } from '@/store/user';
import { useOrganizationStore } from '@/store/organization';
import { getAuthProvider, getAuthProviderName } from '@/auth/AuthFactory';
import type { AccessRequirement, AuthProvider } from '@/auth/AuthProvider';
import { State } from '@/utils';
import type { NavigationGuardNext, NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { Rule } from '@/acl/rules';
import { useAcl } from 'vue-simple-acl';
import { Status } from 'nice-grpc-web';

const { can } = useAcl();

const accessRoutes: Record<AccessRequirement, string> = {
  'verify-email': routes.emailVerification.fullPath,
  'reset-password': routes.resetPassword.fullPath,
  'enroll-mfa': routes.mfa.fullPath,
  'submit-mfa': routes.mfa.children[1].fullPath,
};

const allowedPaths = [
  routes.emailVerification.fullPath,
  routes.resetPassword.fullPath,
  routes.mfa.fullPath,
  routes.mfa.children[0].fullPath,
  routes.termsAndConditions.fullPath,
  routes.organizationsRequest.fullPath,
  routes.organizationsInvites.fullPath,
  routes.organizationsJoin.fullPath,
  routes.serviceUnavailable.fullPath,
];

const loggedIn = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  provider: AuthProvider | null,
) => {
  const userStore = useUserStore();
  userStore.isAuthenticated = true;

  if (allowedPaths.includes(to.path)) return next();
  if (!to.meta.requiresAuth) {
    if (to.path === routes.home.path) {
      return next();
    }
    return next({ path: routes.home.path });
  }
  if (provider && getAuthProviderName() === 'ory' && to.path === routes.login.path && to.query.refresh === 'true') {
    return next();
  }

  if (provider && to.path !== routes.emailVerification.fullPath) {
    const initialRequirement = await provider.checkAccess({ fromAuthScreen: false });
    if (initialRequirement && to.path !== accessRoutes[initialRequirement]) {
      return next({ path: accessRoutes[initialRequirement], replace: true });
    }
  }

  const organizationStore = useOrganizationStore();
  let user;

  try {
    userStore.$patch({ state: State.LOADING });
    user = await userStore.fetchCurrentUser();
  } catch (error) {
    if ((error as { code?: number }).code === Status.PERMISSION_DENIED) {
      organizationStore.organizationImpersonation = '';
      organizationStore.organization = null;
      if (to.path === '/') return next();
      return next('/');
    }

    return next({
      path: routes.serviceUnavailable.path,
      query: { retry: to.fullPath },
      replace: true,
    });
  } finally {
    userStore.$patch({ state: State.READY });
  }

  if (provider) {
    const fromAuthScreen = from.path === routes.login.path || from.path === routes.createUser.path;
    const requirement = await provider.checkAccess({
      fromAuthScreen,
      passwordResetRecommended: user.passwordResetRecommended,
    });
    if (requirement && to.path !== accessRoutes[requirement]) {
      return next({ path: accessRoutes[requirement], replace: true });
    }
  }

  const defaultScreenRedirect = userStore.missionControlSettings.defaultScreen ?? routes.dashboard.path;

  if (!user.acceptedTac && to.path !== routes.termsAndConditions.fullPath) {
    return next({ path: routes.termsAndConditions.fullPath });
  }
  if (!userStore.userHasOrganization && to.path !== routes.organizationsRequest.fullPath) {
    return next({ path: routes.organizationsRequest.fullPath });
  }

  if (
    from.path === routes.login.path &&
    from.redirectedFrom &&
    from.redirectedFrom.path !== to.path &&
    !(from.redirectedFrom.path === routes.home.path && to.path === routes.dashboard.path)
  ) {
    return next(from.redirectedFrom);
  }

  if (
    to.redirectedFrom?.path === routes.home.path &&
    [routes.dashboard.path, routes.devices.path].includes(to.path) &&
    to.path !== defaultScreenRedirect
  ) {
    return next(defaultScreenRedirect);
  }

  return next();
};

const notLoggedIn: NavigationGuardWithThis<void> = (to, _from, next) => {
  if (!to.meta.requiresAuth) return next();

  const query =
    to.path !== routes.home.path
      ? { redirect: to.path, organization: to.query.organization }
      : { organization: to.query.organization };

  if (can(Rule.PASSWORD_AUTH)) {
    return next({ path: routes.login.path, query });
  }
  if (can(Rule.TOKEN_AUTH)) {
    return next({ path: routes.tokenLogin.path, query });
  }
  return next({ path: routes.error.path, replace: true });
};

const authGuard: NavigationGuardWithThis<void> = async (to, from, next) => {
  const userStore = useUserStore();
  const provider = userStore.hasLocalAuthToken || userStore.useLocalTokenAuth ? null : getAuthProvider();

  if (to.path === routes.serviceUnavailable.path) return next();

  let currentUser;
  if (userStore.hasLocalAuthToken) {
    try {
      currentUser = await userStore.fetchCurrentUser();
    } catch {
      await userStore.signout();
    }
  } else if (provider) {
    try {
      currentUser = await provider.getCurrentUser();
    } catch (error) {
      // Abstracted MFA exception block (Ory throws this on checkSession)
      //TODO: disabled MFA temporarily
      //   if (error instanceof OryAal2RequiredError) {
      //     userStore.$patch({ currentUser: null, isAuthenticated: false });
      //     if (to.path === routes.login.path && to.query.refresh === 'true') return next();
      //     if (to.path === routes.mfa.fullPath) return next();

      //     return next({
      //       path: routes.mfa.fullPath,
      //       query: { returnTo: to.fullPath },
      //       replace: true,
      //     });
      //   }

      // Let non-Ory errors bubble up
      if (getAuthProviderName() !== 'ory') return next(error as Error);

      userStore.$patch({ currentUser: null, isAuthenticated: false });
      return next({
        path: routes.serviceUnavailable.path,
        query: { retry: to.fullPath },
        replace: true,
      });
    }
  }

  if (!currentUser) {
    userStore.$patch({ currentUser: null, isAuthenticated: false });

    if (provider && getAuthProviderName() === 'ory' && to.path === routes.emailVerification.fullPath) {
      return next();
    }

    return notLoggedIn(to, from, next);
  }

  return loggedIn(to, from, next, provider);
};

export default authGuard;
